import React from 'react';
/**
 * useGanttDragDrop Hook - Manages drag and drop operations for Gantt chart
 * Epic 46: Added interval validation support
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSensors, useSensor } from '@dnd-kit/core';
import type { DragStartEvent, DragMoveEvent, DragEndEvent } from '@dnd-kit/core';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import jobService from '../services/jobService';
import clienteService, { ValidarIntervaloResponse } from '../services/clienteService';
import type { GanttJob } from './useGanttJobs';
import type { JustificativaIntervalo } from '../components/IntervaloAgendamentoModal';
import {
  minutesToTime,
  timeToMinutes,
  CustomPointerSensor,
  type ResizingJob,
  type PendingOperation,
} from './ganttDragDropUtils';

export { minutesToTime, timeToMinutes, getPosition, getWidth } from './ganttDragDropUtils';

interface UseGanttDragDropReturn {
  activeId: number | null;
  draggedJob: GanttJob | null;
  dragPreviewPosition: number | null;
  resizingJob: ResizingJob | null;
  timelineRef: React.RefObject<HTMLDivElement>;
  timelinePixelWidth: number;
  sensors: ReturnType<typeof useSensors>;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragMove: (event: DragMoveEvent) => void;
  handleDragEnd: (event: DragEndEvent) => Promise<void>;
  handleResizeStart: (e: React.MouseEvent, job: GanttJob, handle: 'left' | 'right') => void;
  // Epic 46: Interval validation
  showIntervaloModal: boolean;
  intervaloValidacoes: ValidarIntervaloResponse[];
  intervaloDataSelecionada: string;
  intervaloLoading: boolean;
  handleIntervaloConfirm: () => Promise<void>;
  // Epic 47: Handler that accepts justifications (for modal interface consistency)
  handleIntervaloConfirmWithJustificativas: (justificativas: JustificativaIntervalo[]) => Promise<void>;
  handleIntervaloCancel: () => void;
}

interface UseGanttDragDropProps {
  jobs: GanttJob[];
  setJobs: React.Dispatch<React.SetStateAction<GanttJob[]>>;
  filteredJobs: GanttJob[];
  fetchJobs: () => Promise<void>;
  startHour: number;
  totalHours: number;
  loading: boolean;
  isBusinessHours: boolean;
  selectedDate: Date; // Epic 46: Required for interval validation
}

export function useGanttDragDrop({
  jobs,
  setJobs,
  filteredJobs,
  fetchJobs,
  startHour,
  totalHours,
  loading,
  isBusinessHours,
  selectedDate,
}: UseGanttDragDropProps): UseGanttDragDropReturn {
  const queryClient = useQueryClient();
  const totalMinutes = totalHours * 60;

  const [activeId, setActiveId] = useState<number | null>(null);
  const [draggedJob, setDraggedJob] = useState<GanttJob | null>(null);
  const [dragPreviewPosition, setDragPreviewPosition] = useState<number | null>(null);
  const [resizingJob, setResizingJob] = useState<ResizingJob | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [timelinePixelWidth, setTimelinePixelWidth] = useState(0);

  // Epic 46: Interval validation state
  const [showIntervaloModal, setShowIntervaloModal] = useState(false);
  const [intervaloValidacoes, setIntervaloValidacoes] = useState<ValidarIntervaloResponse[]>([]);
  const [intervaloDataSelecionada, setIntervaloDataSelecionada] = useState<string>('');
  const [intervaloLoading, setIntervaloLoading] = useState(false);
  const [pendingOperation, setPendingOperation] = useState<PendingOperation | null>(null);

  const sensors = useSensors(
    useSensor(CustomPointerSensor, {
      activationConstraint: { distance: 2 },
    })
  );

  // Measure timeline width
  useEffect(() => {
    const measureTimeline = () => {
      if (timelineRef.current) {
        setTimelinePixelWidth(timelineRef.current.offsetWidth);
      }
    };

    measureTimeline();
    window.addEventListener('resize', measureTimeline);

    const resizeObserver = new ResizeObserver(measureTimeline);
    if (timelineRef.current) {
      resizeObserver.observe(timelineRef.current);
    }

    return () => {
      window.removeEventListener('resize', measureTimeline);
      resizeObserver.disconnect();
    };
  }, [loading, isBusinessHours]);

  // Helper functions
  const pixelsToMinutes = useCallback((pixels: number, timelineWidth: number): number => {
    return Math.round((pixels / timelineWidth) * totalMinutes);
  }, [totalMinutes]);

  const snapToGrid = useCallback((minutes: number): number => {
    return Math.round(minutes / 15) * 15;
  }, []);

  /** IDs dos clientes de um job (multi-cliente, com fallback ao cliente_id legado). */
  const clienteIdsDoJob = (job: GanttJob): number[] =>
    job.clientes?.length ? job.clientes.map(c => c.id) : job.cliente_id ? [job.cliente_id] : [];

  const checkConflict = useCallback((
    jobs: GanttJob[],
    jobId: number,
    newHoraInicio: string,
    newHoraFim: string,
    operadorId: number
  ): boolean => {
    const newStart = timeToMinutes(newHoraInicio);
    const newEnd = timeToMinutes(newHoraFim);
    const jobMovido = jobs.find(j => j.id === jobId);
    const clientesMovido = jobMovido ? clienteIdsDoJob(jobMovido) : [];

    return jobs.some(job => {
      if (job.id === jobId || job.operador_id !== operadorId) return false;
      if (!job.hora_inicio || !job.hora_fim) return false;

      // N73 (Alana): mesmo auditor/horário é permitido entre CLIENTES DIFERENTES.
      // Mesma regra do backend (job_service.validate_auditor_schedule_conflict).
      if (clientesMovido.length) {
        const clientesOutro = clienteIdsDoJob(job);
        if (clientesOutro.length && !clientesOutro.some(id => clientesMovido.includes(id))) return false;
      }

      const jobStart = timeToMinutes(job.hora_inicio);
      const jobEnd = timeToMinutes(job.hora_fim);

      return newStart < jobEnd && newEnd > jobStart;
    });
  }, []);

  // Epic 46: Validate interval for all clients in the job
  const validateIntervalo = useCallback(async (job: GanttJob): Promise<ValidarIntervaloResponse[]> => {
    // Get client IDs from the job
    const clienteIds: number[] = [];

    // From the clientes array (Epic 28: Multiple clients)
    if (job.clientes && job.clientes.length > 0) {
      clienteIds.push(...job.clientes.map(c => c.id));
    }
    // Fallback to single cliente_id
    else if (job.cliente_id) {
      clienteIds.push(job.cliente_id);
    }

    if (clienteIds.length === 0) {
      return []; // No clients to validate
    }

    // Format date for API
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dataPrevista = `${year}-${month}-${day}`;

    try {
      const result = await clienteService.validarIntervaloMultiplosClientes(
        clienteIds,
        job.hospital_id,
        dataPrevista,
        job.id // Exclude current job from validation
      );

      // Return only validations with violations
      return result.validacoes.filter(v => !v.valido && v.violacoes.length > 0);
    } catch (error) {
      console.error('Error validating interval:', error);
      return []; // On error, proceed without blocking
    }
  }, [selectedDate]);

  // Epic 46: Execute the pending save operation
  const executeSaveOperation = useCallback(async (operation: PendingOperation) => {
    const { job, newHoraInicio, newHoraFim, newDuration, type } = operation;

    // Update local state
    setJobs(prev => prev.map(j =>
      j.id === job.id
        ? { ...j, hora_inicio: newHoraInicio, hora_fim: newHoraFim, duracao: newDuration || j.duracao }
        : j
    ));

    try {
      await jobService.rescheduleJob(job.id, newHoraInicio, newHoraFim);
      // Atualiza o Calendario (React Query ['jobs']) sem clicar "Atualizar".
      queryClient.invalidateQueries({ queryKey: ['jobs'], refetchType: 'all' });
      toast.success(type === 'drag' ? 'Contagem reagendada com sucesso!' : 'Duração atualizada com sucesso!');
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || 'Erro ao reagendar contagem';
      toast.error(errorMsg);
      fetchJobs(); // Revert on error
    }
  }, [setJobs, fetchJobs]);

  // Epic 46: Handle confirmation from interval modal (without justifications - legacy)
  const handleIntervaloConfirm = useCallback(async () => {
    if (!pendingOperation) return;

    setIntervaloLoading(true);
    try {
      await executeSaveOperation(pendingOperation);
    } finally {
      setIntervaloLoading(false);
      setShowIntervaloModal(false);
      setPendingOperation(null);
      setIntervaloValidacoes([]);
    }
  }, [pendingOperation, executeSaveOperation]);

  // Epic 47: Handle confirmation from interval modal WITH justifications
  // Note: Gantt reschedule doesn't save justifications (only hora changes), but we support the interface
  const handleIntervaloConfirmWithJustificativas = useCallback(async (
    justificativas: JustificativaIntervalo[]
  ) => {
    if (!pendingOperation) return;

    setIntervaloLoading(true);
    try {
      // Log justifications for debugging (reschedule endpoint doesn't save them)
      console.log('[Epic 47] Gantt reschedule - justifications received (not saved):', justificativas);
      await executeSaveOperation(pendingOperation);
    } finally {
      setIntervaloLoading(false);
      setShowIntervaloModal(false);
      setPendingOperation(null);
      setIntervaloValidacoes([]);
    }
  }, [pendingOperation, executeSaveOperation]);

  // Epic 46: Handle cancellation from interval modal
  const handleIntervaloCancel = useCallback(() => {
    setShowIntervaloModal(false);
    setPendingOperation(null);
    setIntervaloValidacoes([]);
    fetchJobs(); // Revert any optimistic updates
  }, [fetchJobs]);

  // Drag handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const jobId = Number(event.active.id);
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      setActiveId(jobId);
      setDraggedJob(job);
    }
  }, [jobs]);

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    if (!draggedJob || !timelineRef.current) return;

    const timelineWidth = timelineRef.current.offsetWidth;
    const deltaX = event.delta.x;
    const deltaMinutes = pixelsToMinutes(deltaX, timelineWidth);

    const currentMinutes = timeToMinutes(draggedJob.hora_inicio || '07:00');
    const duracao = draggedJob.duracao || 120;

    const newMinutes = snapToGrid(Math.max(0, Math.min(1440 - duracao, currentMinutes + deltaMinutes)));

    setDragPreviewPosition(newMinutes);
  }, [draggedJob, pixelsToMinutes, snapToGrid]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    if (!draggedJob || dragPreviewPosition === null) {
      setActiveId(null);
      setDraggedJob(null);
      setDragPreviewPosition(null);
      return;
    }

    const duracao = draggedJob.duracao || 120;
    const newHoraInicio = minutesToTime(dragPreviewPosition);
    const newHoraFim = minutesToTime(dragPreviewPosition + duracao);

    // Check for time conflicts
    if (checkConflict(filteredJobs, draggedJob.id, newHoraInicio, newHoraFim, draggedJob.operador_id)) {
      toast.error(
        `Conflito de horário detectado!\nO auditor já possui outra contagem neste horário (${newHoraInicio} - ${newHoraFim}).`,
        { duration: 4000 }
      );
      setActiveId(null);
      setDraggedJob(null);
      setDragPreviewPosition(null);
      return;
    }

    // Clear drag state
    setActiveId(null);
    setDraggedJob(null);
    setDragPreviewPosition(null);

    // Epic 46: Validate interval for all clients
    const violations = await validateIntervalo(draggedJob);

    if (violations.length > 0) {
      // Show modal for confirmation
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');

      setPendingOperation({
        type: 'drag',
        job: draggedJob,
        newHoraInicio,
        newHoraFim,
        newDuration: duracao
      });
      setIntervaloValidacoes(violations);
      setIntervaloDataSelecionada(`${year}-${month}-${day}`);
      setShowIntervaloModal(true);
      return;
    }

    // No violations - proceed with save
    await executeSaveOperation({
      type: 'drag',
      job: draggedJob,
      newHoraInicio,
      newHoraFim,
      newDuration: duracao
    });
  }, [draggedJob, dragPreviewPosition, filteredJobs, checkConflict, validateIntervalo, executeSaveOperation, selectedDate]);

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent, job: GanttJob, handle: 'left' | 'right') => {
    e.stopPropagation();
    setResizingJob({ job, handle });
  }, []);

  useEffect(() => {
    if (!resizingJob) return;

    const handleResizeMove = (e: MouseEvent) => {
      if (!resizingJob || !timelineRef.current) return;

      const timelineRect = timelineRef.current.getBoundingClientRect();
      const timelineWidth = timelineRect.width;
      const relativeX = e.clientX - timelineRect.left;

      const mouseMinutes = snapToGrid(pixelsToMinutes(relativeX, timelineWidth) + (startHour * 60));

      const currentStart = timeToMinutes(resizingJob.job.hora_inicio || '07:00');
      const currentDuration = resizingJob.job.duracao || 120;
      const currentEnd = currentStart + currentDuration;

      let newStart = currentStart;
      let newDuration = currentDuration;

      if (resizingJob.handle === 'left') {
        newStart = Math.max(0, Math.min(mouseMinutes, currentEnd - 15));
        newDuration = currentEnd - newStart;
      } else {
        const newEnd = Math.min(1440, Math.max(mouseMinutes, currentStart + 15));
        newDuration = newEnd - currentStart;
      }

      setResizingJob(prev => prev ? { ...prev, previewStart: newStart, previewDuration: newDuration } : null);
    };

    const handleResizeEnd = async () => {
      if (!resizingJob) return;

      const currentStart = timeToMinutes(resizingJob.job.hora_inicio || '07:00');
      const currentDuration = resizingJob.job.duracao || 120;

      const newStart = resizingJob.previewStart ?? currentStart;
      const newDuration = resizingJob.previewDuration ?? currentDuration;

      if (newStart === currentStart && newDuration === currentDuration) {
        setResizingJob(null);
        return;
      }

      const newHoraInicio = minutesToTime(newStart);
      const newHoraFim = minutesToTime(newStart + newDuration);

      if (checkConflict(filteredJobs, resizingJob.job.id, newHoraInicio, newHoraFim, resizingJob.job.operador_id)) {
        toast.error(
          `Conflito de horário detectado!\nO novo horário (${newHoraInicio} - ${newHoraFim}) conflita com outra contagem.`,
          { duration: 4000 }
        );
        setResizingJob(null);
        return;
      }

      // Epic 46: Validate interval for all clients
      const violations = await validateIntervalo(resizingJob.job);
      setResizingJob(null);

      if (violations.length > 0) {
        // Show modal for confirmation
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');

        setPendingOperation({
          type: 'resize',
          job: resizingJob.job,
          newHoraInicio,
          newHoraFim,
          newDuration
        });
        setIntervaloValidacoes(violations);
        setIntervaloDataSelecionada(`${year}-${month}-${day}`);
        setShowIntervaloModal(true);
        return;
      }

      // No violations - proceed with save
      await executeSaveOperation({
        type: 'resize',
        job: resizingJob.job,
        newHoraInicio,
        newHoraFim,
        newDuration
      });
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [resizingJob, filteredJobs, setJobs, startHour, snapToGrid, pixelsToMinutes, checkConflict, validateIntervalo, executeSaveOperation, selectedDate]);

  return {
    activeId,
    draggedJob,
    dragPreviewPosition,
    resizingJob,
    timelineRef,
    timelinePixelWidth,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleResizeStart,
    // Epic 46: Interval validation
    showIntervaloModal,
    intervaloValidacoes,
    intervaloDataSelecionada,
    intervaloLoading,
    handleIntervaloConfirm,
    // Epic 47: Handler that accepts justifications
    handleIntervaloConfirmWithJustificativas,
    handleIntervaloCancel,
  };
}
